import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded
} from 'vue-router'
import {getLdClient, waitForLdReady} from '@/branchmanager/config/ldClientRef'
import {WorkflowGroupStore} from '@/branchmanager/app/workspace/workflowGroupStore.ts'
import {$inj} from '@/common/decorators/depinject.ts'
import {BranchTrackerFactory} from '@/common/services/Applicant/branchTrackerFactory.ts'
import WorkspaceHistoryService, {
  type WorkspaceHistory
} from '@/common/services/WorkspaceHistory/workspaceHistoryService.ts'
import WorkflowFactory from '@/common/services/Workflow/WorkFlowFactory.ts'
import EmployeeChannelWorkflowGroupsFactory from '@/common/services/Workflow/EmployeeChannelWorkflowGroupsFactory.ts'
import {ModalUtil} from '@/common/utils/ModalUtil.ts'

export const WorkspaceStateBeforeEnter = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded
) => {
  const branchTrackerFactory = $inj(BranchTrackerFactory)
  const workspaceHistoryService = $inj(WorkspaceHistoryService)
  const workspaceUUID = to.params.workspaceUUID as string

  WorkflowGroupStore().resetGroup(workspaceUUID)

  return waitForLdReady().then(() => {
    const flagValue = getLdClient()?.variation(
      'open-anywhere-employee-channel-custom-workflows',
      false
    )

    if (flagValue) {
      return Promise.all([
        workspaceHistoryService.getHistory(workspaceUUID),
        branchTrackerFactory.get(workspaceUUID)
      ]).then(([acResponse, ecResponse]) => {
        if (WorkflowGroupStore().activeWorkspaceUUID !== workspaceUUID) return

        WorkflowGroupStore().setWorkspaceHistory(workspaceUUID, acResponse.data.payload)

        if (ecResponse.data.payload) {
          // Employee Channel workflow, 1:1 with workflowGroups
          WorkflowGroupStore().setGroup(ecResponse.data.payload.workflowGroupId, workspaceUUID)
        } else if (acResponse.data.payload) {
          // Applicant channel, many:many association. the AC can visit multiple workflows, and configuration can
          // have the same AC workflow linked to multiple workflowGroups
          const workflows = getWorkflowsFromHistory(acResponse.data.payload)

          return getStartWorkflows(workflows)
            .then(getCompatibleWorkflowGroups)
            .then((response) => {
              if (WorkflowGroupStore().activeWorkspaceUUID !== workspaceUUID) return

              if (response.length == 1 && response[0].workflowGroupId) {
                WorkflowGroupStore().setGroup(response[0].workflowGroupId, workspaceUUID)
              } else if (response.length > 1) {
                const modalUtil = $inj(ModalUtil)
                modalUtil.prompt(
                  "Configuration Error",
                  "More than one workflow has been configured to use this Applicant Channel workflow. Because of this, the default Employee Channel workflow has been chosen instead."
                )
              }
            })
        }
      })
    }
  })
}

const getWorkflowsFromHistory = (histories: Array<WorkspaceHistory>) =>
  Array.from(
    histories.reduce((acc, cur) => {
      if (cur.state?.workflow != null) {
        acc.add(cur.state?.workflow)
      }
      return acc
    }, new Set<string>())
  )

const getStartWorkflows = (workflows: Array<string>) => {
  const workflowFactory = $inj(WorkflowFactory)

  return Promise.all(
    workflows.map((workflow) =>
      workflowFactory.getWorkflow(workflow).then((response) => {
        return response.startWorkflowCode
      })
    )
  )
}

const getCompatibleWorkflowGroups = (startWorkflowCodes: Array<string>) => {
  const employeeChannelWorkflowGroupsFactory = $inj(
    EmployeeChannelWorkflowGroupsFactory
  )

  return employeeChannelWorkflowGroupsFactory
    .getAll()
    .then((response) =>
      response.data.payload
        .filter((x) => x.enabled)
        .filter(
          (startWorkflowGroup) =>
            startWorkflowGroup.applicantChannelWorkflow &&
            startWorkflowCodes.includes(
              startWorkflowGroup.applicantChannelWorkflow
            )
        )
    )
}
